import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TooltipError } from '@components/atoms/Tooltip/Tooltip.styled';
import {
  FieldWrapper,
  InputContainer,
  Required,
  TitleLabel,
  SubLabel,
} from '@components/atoms/Field/Field.styled';
import { InputPhone, InputText } from './TextField.styled';
import './TextField.css';
import { isValidTelNumber, patterns, isEnDateOfBirth } from '@actions/formValidation';
import Icon from '@components/atoms/Icon/Icon';
import { getCountries } from 'react-phone-number-input/input';
import { preferredCountriesOrder, excluded } from '@config/countries';

const TextField = ({
  type,
  name,
  id,
  className,
  value,
  required,
  label,
  sublabel,
  placeholder,
  pattern,
  onChange,
  min,
  max,
}) => {
  const [isInvalid, setIsInvalid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [patternName, setPatternName] = useState(type);
  const [loaded, setLoaded] = useState(false);

  const selectedPattern = patterns[patternName];

  useEffect(() => {
    setLoaded(true);
    if (pattern) {
      setPatternName(pattern);
    }

    if (type === 'tel') {
      changePhoneInputValidityHandler();
    }
  }, []);

  const invalid = (input) => {
    let value = input?.target?.value || '';

    if (selectedPattern) {
      const regex = new RegExp(selectedPattern);
      setIsInvalid(
        input.type && input.type === 'invalid' && !regex.test(value)
      );
    } else {
      if (input.target === 'tel') {
        const isValidPhoneNumber = isValidTelNumber(value);
        setIsInvalid(
          input.type && input.type === 'invalid' && !isValidPhoneNumber
        );
      } else {
        setIsInvalid(input.type && input.type === 'invalid');
      }
    }
    setErrorMessage('Ce champ est nécessaire.');

    let message = 'Ce champ est requis.';
    if (input.target.value !== '') {
      switch (type) {
        case 'email':
          message = 'Adresse mail invalide.';
          break;
        case 'tel':
          message = 'Téléphone invalide.';
          break;
        default:
          message = `${
            label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
          } invalide.`;
          break;
      }
    }
    setErrorMessage(message);

    const inputField = document.querySelector('#' + id);
    inputField.setCustomValidity(message);
  };

  const changePhoneInputValidityHandler = (value) => {
    const input = document.querySelector('#' + id);
    let message = 'Téléphone invalide';
    if (!value || !input.value) {
      message = 'Téléphone requis';
    }

    if (!value) {
      value = input.value;
    }
    input.setCustomValidity(!isValidTelNumber(value) ? message : '');
    setErrorMessage(message);

    if (loaded) {
      setIsInvalid(!isValidTelNumber(value));
    }
  };

  const onChangeHandler = (event) => {
    let data = event;
    let value = event?.target?.value || '';
    const eventType = event?.type || null;

    if (type === 'tel' && typeof value === 'string') {
      if (!eventType) {
        value = event || '';
      }
      value = value
        .replace('+330', '+33')
        .replace('+33 0', '+33')
        .replace('+5900', '+590')
        .replace('+590 0', '+590')
        .replace('+5940', '+594')
        .replace('+594 0', '+594')
        .replace('+2620', '+262')
        .replace('+262 0', '+262')
        .replace('+5960', '+596')
        .replace('+596 0', '+596')
        .replace('+2690', '+269')
        .replace('+269 0', '+269')
        .replace('+6870', '+687')
        .replace('+687 0', '+687')
        .replace('+6890', '+689')
        .replace('+689 0', '+689')
        .replace('+5080', '+508')
        .replace('+508 0', '+508')
        .replace('+6810', '+681')
        .replace('+681 0', '+681');
      data = { id, target: { id, value } };
      changePhoneInputValidityHandler(value);
    }

    if (type !== 'tel' && type !== 'date') {
      const regex = new RegExp(selectedPattern);
      setIsInvalid(false);
      let message;
      switch (type) {
        case 'email':
          // if (regex.test(event.target.value)) {
          // }
          regex.test(event.target.value)
            ? setIsInvalid(false)
            : setIsInvalid(true);
          message = 'Adresse mail invalide.';
          break;
        default:
          message = `${
            label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
          } invalide.`;
          break;
      }
      setErrorMessage(message);
    }

    if (type === 'date') {
      if (!isEnDateOfBirth(value)) {
        setIsInvalid(true);
        setErrorMessage('Date de naissance invalide.');
      } else {
        setIsInvalid(false);
        setErrorMessage('');
      }
    }

    if (selectedPattern) {
      try {
        const regex = new RegExp(selectedPattern);
        setIsInvalid(!regex.test(value));
      } catch (error) {
        console.error("error while checking regex", error);
      }
    }

    if (value === '') {
      setIsInvalid(true);
      setErrorMessage('Ce champ est requis.');
    }

    onChange(data);
  };

  return (
    <FieldWrapper data-testid={`textfield_wrapper_${id}`}>
      {(type !== 'hidden' && (
        <>
          <TitleLabel
            htmlFor={id}
            $iserror={isInvalid || undefined}
          >
            {label}
            {sublabel && <SubLabel>{sublabel}</SubLabel>}
            {required && <Required />}
          </TitleLabel>
          {(isInvalid && (
            <TooltipError
              data-testid={`textfield_error_${id}`}
              $content={errorMessage}
              $type={type}
            />
          )) || <></>}
          {(isInvalid && <Icon name='cross' />) || <></>}
          {(!isInvalid && isInvalid !== null && value !== '' && (
            <Icon name='check' />
          )) || <></>}

          <InputContainer>
            {(type === 'tel' && (
              <InputPhone
                type={type}
                countries={getCountries().filter(
                  (countryCode) => !excluded.includes(countryCode)
                )}
                countryOptionsOrder={preferredCountriesOrder}
                id={id}
                name={name}
                required={required}
                defaultCountry='FR'
                international
                iserror={isInvalid ? isInvalid.toString() : ''}
                limitMaxLength={true}
                withCountryCallingCode
                placeholder={placeholder}
                value={value}
                onInvalid={invalid.bind(this)}
                onChange={onChangeHandler.bind(this)}
                data-testid={`textfield_${id}`}
              />
            )) || (
              <InputText
                type={type}
                id={id}
                name={name}
                required={required}
                className={className}
                value={value}
                placeholder={placeholder}
                $iserror={isInvalid || undefined}
                onChange={onChangeHandler.bind(this)}
                onKeyUp={onChangeHandler.bind(this)}
                onBlur={onChangeHandler.bind(this)}
                onInvalid={invalid.bind(this)}
                data-testid={`textfield_${id}`}
                min={min}
                max={max}
              />
            )}
          </InputContainer>
        </>
      )) || (
        <InputText
          required={required}
          type={type}
          title=''
          id={id}
          $iserror={isInvalid || undefined}
          name={name}
          className={className}
          value={value}
          data-testid={`textfield_hidden_${id}`}
        />
      )}
    </FieldWrapper>
  );
};

TextField.propTypes = {
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
  label: PropTypes.string,
  sublabel: PropTypes.string,
  pattern: PropTypes.string,
  onChange: PropTypes.func,
};

export default TextField;
